
import { useRef } from 'react';
import { useStarData } from './useStarData';
import { useStarUI } from './useStarUI';
import { useThemeManager } from './useThemeManager';
import { useTaskManager } from './useTaskManager';
import { useRewardManager } from './useRewardManager';
import { useAchievementManager } from './useAchievementManager';
import { useWishListManager } from './useWishListManager';
import { useFirebase } from '@/providers/FirebaseProvider';
import { ThemeName } from '@/utils/storage';

export const useStarTracker = (initialTab: string = 'tasks', onPerfectDay?: (theme: string) => void) => {
  const starsCounterRef = useRef<HTMLDivElement>(null);

  // Get user for activity logging
  const { user } = useFirebase();

  // Initialize hooks
  const {
    data,
    updateData,
    resetAppData,
    updateStars
  } = useStarData();
  
  const {
    editMode,
    activeTab,
    showResetDialog,
    animateStars,
    showGiantStar,
    isAnimating,
    adsEnabled,
    setEditMode,
    setActiveTab,
    setShowResetDialog,
    setAnimateStars,
    setShowGiantStar,
    setIsAnimating,
    setAdsEnabled,
    handleToggleEditMode,
    handleGiantStarAnimationComplete
  } = useStarUI(initialTab);
  
  const { 
    applyTheme,
    getThemeEmoji 
  } = useThemeManager(data?.theme);
  
  const {
    handleCompleteTask,
    handleUpdateStars,
    handleMoveTask,
    handleResetDailyTasks,
    handleDeleteTask,
    handleResetTask,
    getTotalDailyStars,
    getCompletedTasksCount,
    getUncompletedTasksCount
  } = useTaskManager(data, updateData, setIsAnimating, setShowGiantStar, user, onPerfectDay);

  const {
    handleClaimReward,
    handleSpendMoney,
    handleDeleteReward,
    handleMoveReward
  } = useRewardManager(data, updateData, setIsAnimating, setShowGiantStar, user);
  
  const {
    handleDeleteAchievement,
    handleMoveAchievement
  } = useAchievementManager(data, updateData);

  const {
    wishList,
    wishListEnabled,
    addWishItem,
    updateWishItem,
    deleteWishItem,
    markWishItemAchieved,
    updateWishItemProgress,
    toggleWishListEnabled,
    getWishListStats
  } = useWishListManager();
  
  // Enhanced toggle edit mode to handle tab switching
  const handleEnhancedToggleEditMode = () => {
    if (editMode) {
      // If we're exiting edit mode and currently on admin tab, switch to tasks
      if (activeTab === 'admin') {
        setActiveTab('tasks');
      }
      setEditMode(false);
    } else {
      handleToggleEditMode();
    }
  };
  
  // Combined functions
  const handleThemeChange = async (theme: ThemeName) => {
    if (!data) return;
    
    try {
      // Apply theme immediately for visual feedback
      applyTheme(theme);
      
      const newData = { ...data, theme };
      await updateData(newData);

      console.log(`[useStarTracker] Theme changed to ${theme} and saved successfully`);
    } catch (error) {
      console.error('[useStarTracker] Error updating theme:', error);
      // Revert theme if save failed
      applyTheme(data.theme);
    }
  };
  
  const handleDeleteItem = (id: string, type: 'task' | 'reward' | 'achievement') => {
    if (type === 'task') {
      handleDeleteTask(id);
    } else if (type === 'reward') {
      handleDeleteReward(id);
    } else if (type === 'achievement') {
      handleDeleteAchievement(id);
    }
  };
  
  const handleResetData = () => {
    resetAppData();
    setShowResetDialog(false);
  };
  
  const handleUpdateTotalStars = (change: number) => {
    if (!editMode || !data) return;
    updateStars(data.stars + change);
  };

  return {
    data,
    updateData,
    editMode,
    activeTab,
    showResetDialog,
    animateStars,
    showGiantStar,
    starsCounterRef,
    isAnimating,
    adsEnabled,
    setEditMode,
    setActiveTab,
    setShowResetDialog,
    setAnimateStars,
    setShowGiantStar,
    setAdsEnabled,
    handleToggleEditMode: handleEnhancedToggleEditMode,
    handleCompleteTask,
    handleClaimReward,
    handleSpendMoney,
    handleThemeChange,
    handleDeleteItem,
    handleResetData,
    handleUpdateStars,
    handleUpdateTotalStars,
    handleResetDailyTasks,
    handleResetTask,
    handleMoveTask,
    handleMoveReward,
    handleMoveAchievement,
    getTotalDailyStars,
    getCompletedTasksCount,
    getUncompletedTasksCount,
    getThemeEmoji,
    handleGiantStarAnimationComplete,
    // Wish List functionality
    wishList,
    wishListEnabled,
    addWishItem,
    updateWishItem,
    deleteWishItem,
    markWishItemAchieved,
    updateWishItemProgress,
    toggleWishListEnabled,
    getWishListStats
  };
};
